import { infiniteQueryOptions, type InfiniteData, type QueryClient } from "@tanstack/react-query";
import type { ProjectService } from "@/services/project.service";
import type {
  TWorkspaceProjectRow,
  TWorkspaceProjectsListQuery,
  TWorkspaceProjectsPage,
} from "@/types/project";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The Projects table's pages (NG-2804): `{ws}/projects/` filtered, sorted and
 * grouped server-side and walked by cursor, `[slug, uid, 'projects', 'page', {…}]`.
 * Mutable, because it is the screen where people edit those rows.
 *
 * It sits under `'projects'` beside the bank (R2), so one prefix invalidation
 * reaches both, and under `'page'` so a writer can reach every filter and sort
 * of the table without naming one. The filter and sort state stays in
 * `ProjectStore` and becomes the key (decision 4, 2026-10-13); this file only
 * reads.
 *
 * @writers The table's own writers (`useProjectsTable().refresh`): create,
 * import, duplicate and the bulk status, lead, archive and delete actions mark
 * every page under {@link projectsTablePrefix} stale and re-read the one on
 * screen. The project writers in `queries/projects.ts` keep its rows in step
 * with a write: `patchProject` patches the row by id
 * ({@link patchProjectsTableRows}), `forgetProject` removes it
 * ({@link removeProjectsTableRows}), and `appendProject` re-reads the table
 * ({@link invalidateProjectsTable}). An optimistic edit holds the rows it
 * changes ({@link holdProjectsTableRows}) to put them back on failure. Another
 * tab's project write reaches it through the `projects:bank` hint
 * (`stores/cache-hints/projects.ts`).
 */

/** The parameters that change the response body; the cursor is the page param. */
export type ProjectsTableQuery = Omit<TWorkspaceProjectsListQuery, "cursor">;

/** The list parameters that are sets: their order does not change the rows. */
const SET_PARAMS = [
  "state",
  "category",
  "lead",
  "delivery_teams",
  "home_team",
  "client_teams",
  "programs",
  "goals",
  "process",
  "stage",
  "custom_field",
] as const satisfies readonly (keyof ProjectsTableQuery)[];

/**
 * The table key. Absent and empty parameters send the same request, so they
 * share an entry, and a set picked in another order does too.
 */
export function projectsTableKey(scope: QueryScope, query: ProjectsTableQuery = {}) {
  const params: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(query)) {
    if (value === undefined || value === "" || value === false) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      params[name] = (SET_PARAMS as readonly string[]).includes(name) ? [...value].sort() : value;
      continue;
    }
    params[name] = value;
  }
  return scopedKey(scope, "projects", "page", params);
}

/** Every table key in `scope`, whatever the filter and sort: a writer's prefix. */
export function projectsTablePrefix(scope: QueryScope) {
  return scopedKey(scope, "projects", "page");
}

/**
 * The table's pages for one filter, sort and group-by. The first page asks with
 * no cursor; each later page asks with the cursor the previous one returned, and
 * there is no next page once the server says so.
 */
export function projectsTableQuery(
  service: Pick<ProjectService, "list">,
  scope: QueryScope,
  query: ProjectsTableQuery = {},
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey: projectsTableKey(scope, query),
    initialPageParam: null as string | null,
    queryFn: scopedRead<TWorkspaceProjectsPage, string | null>(
      (request, { pageParam }) =>
        service.list(scope.tenant, pageParam ? { ...query, cursor: pageParam } : query, request),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: TWorkspaceProjectsPage) =>
      last.next_page_results && last.next_cursor ? last.next_cursor : undefined,
  });
}

/**
 * Every row in the table's pages, once each, in page order: a row can appear
 * on two pages when the list shifts between reads, and the table keys rows by
 * id. A row is its project in its group: grouped by an M2M dimension, the
 * server lists a project once per group it belongs to (#1437), each copy
 * annotated with that `group`, and every copy is kept.
 */
export function rowsOfProjectsTablePages(
  pages: readonly TWorkspaceProjectsPage[],
): TWorkspaceProjectRow[] {
  const seen = new Set<string>();
  const rows: TWorkspaceProjectRow[] = [];
  for (const page of pages) {
    for (const row of page.results) {
      const identity = `${row.id}\u0000${row.group?.id ?? ""}`;
      if (seen.has(identity)) continue;
      seen.add(identity);
      rows.push(row);
    }
  }
  return rows;
}

type ProjectsTablePages = InfiniteData<TWorkspaceProjectsPage, string | null>;

/**
 * Every cached table entry in `scope`, after cancelling the reads in flight: a
 * page read before the write landed would otherwise draw the row back as it was.
 * A cancelled refetch keeps the pages it had, and the entry is marked stale
 * below, so the next mount, focus or poll reads the rows again.
 */
function tablesOf(client: QueryClient, scope: QueryScope) {
  const tables = client.getQueryCache().findAll({ queryKey: projectsTablePrefix(scope) });
  for (const table of tables) {
    if (table.state.fetchStatus === "fetching") {
      void client.cancelQueries({ queryKey: table.queryKey, exact: true });
    }
  }
  return tables;
}

/** `pages` with each row `edit` returns changed, or `pages` itself when none did. */
function mapRows(
  pages: ProjectsTablePages,
  edit: (row: TWorkspaceProjectRow) => TWorkspaceProjectRow | null,
): ProjectsTablePages {
  let changed = false;
  const next = pages.pages.map((page) => {
    let removed = 0;
    let pageChanged = false;
    const results: TWorkspaceProjectRow[] = [];
    for (const row of page.results) {
      const edited = edit(row);
      if (edited === row) {
        results.push(row);
        continue;
      }
      pageChanged = true;
      if (edited) results.push(edited);
      else removed += 1;
    }
    if (!pageChanged) return page;
    changed = true;
    return { ...page, results, total_count: Math.max(0, page.total_count - removed) };
  });
  return changed ? { ...pages, pages: next } : pages;
}

/** Write `edit` into every cached table in `scope`, and mark the ones it changed stale. */
function editTables(
  client: QueryClient,
  scope: QueryScope,
  edit: (row: TWorkspaceProjectRow) => TWorkspaceProjectRow | null,
): void {
  for (const table of tablesOf(client, scope)) {
    const pages = client.getQueryData<ProjectsTablePages>(table.queryKey);
    if (!pages) continue;
    const next = mapRows(pages, edit);
    if (next === pages) continue;
    client.setQueryData(table.queryKey, next);
    void client.invalidateQueries({ queryKey: table.queryKey, exact: true, refetchType: "none" });
  }
}

/**
 * Merge `patch` into the project's rows in every cached table, every filter and
 * group, with no request. A row is still where the server put it: a write that
 * moves it into or out of a filter shows on the table's next read.
 */
export function patchProjectsTableRows(
  client: QueryClient,
  scope: QueryScope,
  projectId: string,
  patch: Partial<TWorkspaceProjectRow>,
): void {
  // The row's group is where it is listed, not a project field.
  const { group: _group, ...fields } = patch;
  void _group;
  editTables(client, scope, (row) => (row.id === projectId ? { ...row, ...fields } : row));
}

/** Take the project's rows out of every cached table, after a delete or a denial. */
export function removeProjectsTableRows(
  client: QueryClient,
  scope: QueryScope,
  projectId: string,
): void {
  editTables(client, scope, (row) => (row.id === projectId ? null : row));
}

/**
 * The project's rows as every cached table holds them now. The returned
 * function writes them back, for an optimistic edit the server refused.
 */
export function holdProjectsTableRows(
  client: QueryClient,
  scope: QueryScope,
  projectId: string,
): () => void {
  const held = new Map<string, TWorkspaceProjectRow>();
  for (const [, pages] of client.getQueriesData<ProjectsTablePages>({
    queryKey: projectsTablePrefix(scope),
  })) {
    for (const row of pages?.pages.flatMap((page) => page.results) ?? []) {
      if (row.id === projectId) held.set(row.group?.id ?? "", row);
    }
  }
  return () => {
    if (held.size === 0) return;
    editTables(client, scope, (row) =>
      row.id === projectId ? (held.get(row.group?.id ?? "") ?? row) : row,
    );
  };
}

/**
 * Read every table on screen again, and mark the rest stale: after a create or
 * a duplicate, whose new row's place in each filter and sort only the server
 * knows.
 */
export function invalidateProjectsTable(client: QueryClient, scope: QueryScope): Promise<void> {
  return client.invalidateQueries({ queryKey: projectsTablePrefix(scope) });
}

/**
 * The project's row in any cached table in `scope`, whatever the filter, for a
 * reader that only needs what the row says (the sidebar names a project
 * starred from the table by it). No request.
 */
export function cachedProjectsTableRow(
  client: QueryClient,
  scope: QueryScope,
  projectId: string,
): TWorkspaceProjectRow | undefined {
  for (const [, pages] of client.getQueriesData<ProjectsTablePages>({
    queryKey: projectsTablePrefix(scope),
  })) {
    for (const page of pages?.pages ?? []) {
      const row = page.results.find((r) => r.id === projectId);
      if (row) return row;
    }
  }
  return undefined;
}
